import * as Sentry from "@sentry/react";

/**
 * Sentry 초기화 및 설정
 * 오류 추적 및 모니터링을 위한 Sentry 설정
 */

export const initSentry = () => {
  // 개발 환경과 프로덕션 환경 모두에서 초기화하도록 변경
  Sentry.init({
    dsn: "https://44e931dda7055e1a39e5320e72aa57e4@o4509309597384704.ingest.us.sentry.io/4509309600923648",
    
    // 사용자 IP 주소 등 기본 PII 데이터 전송 허용
    sendDefaultPii: true,
    
    // 환경 정보 설정
    environment: import.meta.env.PROD ? "production" : "development",
    
    // 오류 발생 시 콘솔에 debug 정보 출력 (개발 환경에서만 활성화)
    debug: import.meta.env.DEV,
    
    // 성능 모니터링 기능은 비활성화 (tracesSampleRate를 0으로 설정)
    tracesSampleRate: 0,
    
    // 개발 환경에서는 모든 에러를 보내도록 설정
    beforeSend(event, hint) {
      console.log('[Sentry] 에러 발생:', hint.originalException);
      return event;
    },
  });
  
  if (import.meta.env.DEV) {
    console.log('[Sentry] 개발 환경에서 초기화되었습니다.');
  }
};

// React 컴포넌트에서 발생하는 오류를 캡처하는 에러 바운더리
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// 사용자 정보 설정 헬퍼 함수
export const setSentryUser = (user: { id: string, email?: string, name?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
};

// 사용자 정보 초기화 (로그아웃 시 호출)
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// 태그 설정 헬퍼 함수
export const setSentryTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

// 커스텀 에러 로깅 함수
export const logSentryError = (error: Error, extras?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: extras,
  });
};

/**
 * 통합 로깅 유틸리티 함수
 * 개발 환경에서는 console.error를 사용하고
 * 프로덕션 환경에서는 Sentry로 오류를 전송
 * 
 * @param message 에러 메시지 또는 에러 객체
 * @param optionalParams 추가 정보 (객체, 문자열 등)
 */
export const logError = (message: unknown, ...optionalParams: unknown[]) => {
  // 개발 환경에서는 console.error 사용
  if (import.meta.env.DEV) {
    console.error(message, ...optionalParams);
    return;
  }
  
  // 프로덕션 환경에서는 Sentry로 전송
  if (message instanceof Error) {
    // 에러 객체인 경우
    Sentry.captureException(message, {
      extra: optionalParams.length > 0 ? { additionalInfo: optionalParams } : undefined
    });
  } else {
    // 에러 객체가 아닌 경우 (문자열 등)
    let errorObject: Error;
    
    if (typeof message === 'string') {
      errorObject = new Error(message);
    } else {
      try {
        errorObject = new Error(JSON.stringify(message));
      } catch {
        errorObject = new Error(String(message));
      }
    }
    
    Sentry.captureException(errorObject, {
      extra: { 
        originalMessage: message,
        additionalInfo: optionalParams.length > 0 ? optionalParams : undefined
      }
    });
  }
}; 